import { FormProvider, useForm, useWatch } from 'react-hook-form';
import PropTypes from 'prop-types';
import Select from '../core/form/Select';
import { useEffect, useState } from 'react';
import { useNode, usePathwayByCaseId } from '@/stores/comparisonStore';
import { getIOOptions, getNodeOptions } from '@/utils/pathwayUtils';
import debounce from 'debounce';
import { ComparisonCaseActions } from './ComparisonCaseActions';
import { Bars3Icon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const isDefaultName = name => {
  return /^Compare \d+$/.test(name);
};

const truncatePathwayName = name => {
  if (!name) return '';
  if (name.length <= 40) return name;
  return name.substring(0, 40) + '...';
};

const ComparisonCaseForm = ({ defaultValues, onSubmit, pathwaysOptions, rowProps, isOver = false }) => {
  const [isDraggable, setIsDraggable] = useState(false);
  const methods = useForm({ defaultValues });
  const { handleSubmit, reset, watch, register, control, setValue } = methods;
  const debouncedSave = debounce(() => handleSubmit(onSubmit)(), 10);
  const [caseId, nodeId, visible, name] = useWatch({
    control,
    name: ['case_id', 'node_id', 'visible', 'name'],
    defaultValue: defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const pathway = usePathwayByCaseId(caseId);
  const nodes = Object.values(pathway?.nodes ?? {});
  const nodeOptions = [{ label: 'Choose one', value: '' }, ...getNodeOptions(nodes)];
  const defaultNodeId = nodeOptions?.length === 1 ? nodes?.[0]?.id : null;
  const currentNode = useNode(caseId, nodeId);
  const outputOptions = getIOOptions(currentNode);
  const defaultOutputName = outputOptions?.length === 1 ? outputOptions?.[0]?.value : null;

  useEffect(() => {
    if (caseId !== defaultValues.case_id && defaultNodeId) {
      setValue('node_id', defaultNodeId);
    }
  }, [caseId, defaultValues.case_id, defaultNodeId, setValue]);

  useEffect(() => {
    if (nodeId !== defaultValues.node_id) {
      setValue('output_name', defaultOutputName);
    }
  }, [nodeId, defaultValues.node_id, defaultOutputName, setValue]);

  useEffect(() => {
    if (caseId && pathway?.name && isDefaultName(name)) {
      const truncatedName = truncatePathwayName(pathway.name);
      setValue('name', truncatedName);
    }
  }, [caseId, pathway?.name, name, setValue]);

  useEffect(() => {
    const subscription = watch(debouncedSave);
    return () => subscription.unsubscribe();
  }, [watch, debouncedSave]);

  const events = {
    onChange: e => e.preventDefault(),
    onBlur: e => setValue('name', e.target.value),
  };

  const handleMouseDown = () => setIsDraggable(true);
  const handleDragEnd = e => {
    setIsDraggable(false);
    if (rowProps?.onDragEnd) rowProps?.onDragEnd(e);
  };
  const handleMouseLeave = () => setIsDraggable(false);

  return (
    <FormProvider {...methods}>
      <tr
        {...rowProps}
        draggable={isDraggable}
        onDragEnd={handleDragEnd}
        onMouseLeave={handleMouseLeave}
        className={`${isOver ? 'bg-gray-100' : ''} ${
          isDraggable ? (isOver ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-default'
        }`}
      >
        <td className="p-1 pl-4">
          <div className="flex items-center gap-4">
            <button
              type="button"
              className="focus:outline-none focus:ring-0 focus:ring-offset-0 cursor-grab"
              onMouseDown={handleMouseDown}
              tabIndex={0}
              aria-label="Drag to reorder"
            >
              <Bars3Icon className={`h-5 w-5 text-zinc-400`} />
            </button>
          </div>
        </td>
        <td className="p-1 comparison-case-name">
          <input
            {...register('name')}
            {...events}
            name="name"
            type="text"
            className="input-bordered input-sm w-3/4 rounded-none truncate"
          />
        </td>
        <td className="p-1">
          <div className="flex">
            <Select name="case_id" className="select-sm max-w-[80%] flex-1" options={pathwaysOptions} />
          </div>
        </td>
        <td>
          <div className="flex">
            <Select name="node_id" className="select-sm max-w-[80%] flex-1" options={nodeOptions} />
          </div>
        </td>
        <td>
          <div className="flex">
            <Select name="output_name" className="select-sm max-w-[80%] flex-1" options={outputOptions} />
          </div>
        </td>
        <td className="pr-8">
          <div className="flex items-center gap-1">
            <label className="cursor-pointer select-none ">
              {visible ? <EyeIcon className="h-5 w-5" /> : <EyeSlashIcon className="h-5 w-5 text-zinc-400" />}
              <input
                type="checkbox"
                className="hidden"
                defaultChecked={true}
                {...register('visible')}
                onChange={e => setValue('visible', e.target.checked)}
              />
            </label>
            <ComparisonCaseActions comparisonCase={defaultValues} />
          </div>
        </td>
      </tr>
    </FormProvider>
  );
};

ComparisonCaseForm.propTypes = {
  defaultValues: PropTypes.object,
  pathwaysOptions: PropTypes.arrayOf(PropTypes.object),
  onSubmit: PropTypes.func,
  rowProps: PropTypes.shape({
    onDragEnd: PropTypes.func,
  }),
  isOver: PropTypes.bool,
};

export default ComparisonCaseForm;
